import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useCart } from '../lib/store';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { HealthStatusBadge } from './HealthStatusBadge';
import { PLATFORM, BASE, allApps } from '../lib/platform';
import {
  LayoutDashboard, Search, ShoppingCart, FileText, Settings, LogOut, Menu, X, Home, AlertCircle,
  Plus, Truck, Package, FileText as FileText2, QrCode, BarChart3, ClipboardList,
  ChevronDown, LayoutGrid, Users,
} from 'lucide-react';

// ─── Primary nav. Order, role gating, and testids intentionally preserved
// from the previous layouts so any deep link / QA selector keeps working.
// "Price List" is the QR section renamed — same route, same component,
// same role gating (canViewQr).
const PRIMARY_NAV = [
  { path: '/',            label: 'Dashboard',  icon: LayoutDashboard },
  { path: '/products',    label: 'Products',   icon: Search },
  { path: '/cart',        label: 'Quotation',  icon: ShoppingCart,    showBadge: true },
  { path: '/quotations',  label: 'History',    icon: FileText },
];

// The header logo: the workspace's own logo on the platform when it has one
// (announced by /help-widget.js), else the Heda logo.
function BrandLogo(props) {
  const [logo, setLogo] = React.useState(() => (PLATFORM && window.__hedaBrand?.logo) || null);
  React.useEffect(() => {
    if (!PLATFORM) return undefined;
    const on = (e) => setLogo(e.detail?.logo || null);
    window.addEventListener('heda-brand', on);
    return () => window.removeEventListener('heda-brand', on);
  }, []);
  return <img src={logo || `${BASE}/heda-logo.png`} alt="HEDA" {...props} />;
}

// One tab of the desktop nav row (.heda-tab from the design preset).
function NavTab({ active, onClick, icon: Icon, label, testId, badge }) {
  return (
    <button type="button" onClick={onClick} className={`heda-tab ${active ? 'is-on' : ''}`} data-testid={testId}>
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <Icon size={13} /> {label}
        {badge > 0 && (
          <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-heda-gold px-1 text-[10px] font-bold text-white">
            {badge}
          </span>
        )}
      </span>
    </button>
  );
}

// One row of the phone menu (hamburger).
function MenuRow({ icon: Icon, children, onClick, testId, className = 'text-heda-ink' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition hover:bg-heda-cream ${className}`}
      data-testid={testId}
    >
      <Icon size={15} className="text-heda-mute" /> {children}
    </button>
  );
}

export default function Layout({ children }) {
  const { user, logout, isAdmin, can, canOpenAdmin, canViewQr } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  const go = (path) => { navigate(path); setMobileOpen(false); };

  // Display name for the user-menu trigger: the first name (the email's
  // name part when there is no name), first character capitalised.
  const displayName = (() => {
    const raw = ((user?.name || '').trim().split(/\s+/)[0] || user?.email?.split('@')[0] || '').trim();
    return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Account';
  })();

  // ─── Role-based visibility (rules unchanged from the previous nav).
  // My Registrations is hidden for admins — admins use the Admin
  // panel's Walk-ins/Leads tabs, which have wider filters + edit powers.
  const showMyRegistrations = !isAdmin;
  // Team follow-ups: every salesperson (staff / creator / manager), admins,
  // and anyone who sees the whole lead register. Same rule as the API.
  const showTeamFollowups = ['staff', 'creator', 'manager', 'admin'].includes(user?.role)
    || can('leads.view_all');

  return (
    <div className="min-h-screen bg-heda-cream" data-testid="app-layout">
      {/* ─── STICKY HEADER (the Visualiser's admin header) ───────────── */}
      <header
        className="safe-top sticky top-0 z-30 border-b border-heda-line bg-heda-cream/95 backdrop-blur"
        data-testid="app-header"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-8 md:py-4">
          <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="inline-flex h-10 min-h-0 w-10 shrink-0 items-center justify-center rounded-full border border-heda-line bg-white text-heda-ink transition hover:border-heda-gold hover:text-heda-gold sm:hidden"
              data-testid="mobile-menu-btn"
              aria-label="Toggle navigation menu"
            >
              {mobileOpen ? <X size={15} /> : <Menu size={15} />}
            </button>
            <button
              type="button"
              className="min-h-0 shrink-0"
              onClick={() => navigate('/')}
              data-testid="nav-logo-btn"
            >
              <BrandLogo className="h-9 w-auto sm:h-11" data-testid="nav-logo" />
            </button>
            <div className="min-w-0 leading-tight">
              <h1 className="font-display text-[17px] font-bold text-heda-ink sm:text-[19px]">Quotations</h1>
              <p className="truncate text-[10.5px] text-heda-mute">
                <span className="hidden sm:inline">Heda Plywoods · </span>Signed in as{' '}
                <span className="font-semibold text-heda-ink/85">{user?.name || displayName}</span>
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* Heda platform only: back to the launcher. */}
            {PLATFORM && (
              <button type="button" onClick={allApps} className="heda-pill hidden sm:inline-flex" data-testid="nav-all-apps">
                <LayoutGrid size={12} /> All apps
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="heda-pill min-h-0 !py-1 !pl-1 max-sm:h-10" data-testid="user-menu-btn">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-heda-ink text-[11px] font-semibold text-white">
                    {displayName.charAt(0)}
                  </span>
                  <span className="hidden whitespace-nowrap sm:inline">{displayName}</span>
                  <ChevronDown size={12} className="opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-xl" data-testid="user-menu">
                <DropdownMenuLabel className="truncate text-xs font-normal text-heda-mute">
                  {user?.email || 'Signed in'}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => navigate('/my-analytics')} data-testid="user-menu-my-analytics">
                  <BarChart3 className="mr-2 h-4 w-4" /> My Analytics
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-complaints')} data-testid="user-menu-my-complaints">
                  <AlertCircle className="mr-2 h-4 w-4" /> My Complaints
                </DropdownMenuItem>
                {showMyRegistrations && (
                  <DropdownMenuItem onClick={() => navigate('/my-registrations')} data-testid="user-menu-my-registrations">
                    <ClipboardList className="mr-2 h-4 w-4" /> My List
                  </DropdownMenuItem>
                )}
                {showTeamFollowups && (
                  <DropdownMenuItem onClick={() => navigate('/team-followups')} data-testid="user-menu-team-followups">
                    <Users className="mr-2 h-4 w-4" /> Team follow-ups
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="text-red-700 focus:text-red-700"
                  data-testid="user-menu-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button type="button" onClick={logout} className="heda-pill-ink min-h-0 max-sm:h-10 max-sm:w-10 max-sm:justify-center" title="Logout" data-testid="header-logout">
              <LogOut size={12} /> <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* ─── Main nav: .heda-tab row (tablet and desktop). Dashboard ·
            Products · Quotation · History · Delivery · Price List ·
            Purchase Orders · Admin. Delivery is visible to all signed-in
            users; Price List gated by canViewQr; Purchase Orders by
            purchase_orders.manage; Admin by canOpenAdmin. */}
        <nav
          className="mx-auto hidden max-w-7xl items-center gap-1 overflow-x-auto border-t border-heda-line/60 px-4 sm:flex md:px-8"
          data-testid="desktop-primary-nav"
        >
          {PRIMARY_NAV.map(({ path, label, icon, showBadge }) => (
            <NavTab
              key={path}
              active={isActive(path)}
              onClick={() => navigate(path)}
              icon={icon}
              label={label}
              testId={`nav-${label.toLowerCase()}`}
              badge={showBadge ? items.length : 0}
            />
          ))}
          {/* Testid kept as nav-more-delivery from the old "More" menu. */}
          <NavTab active={isActive('/delivery')} onClick={() => navigate('/delivery')} icon={Truck} label="Delivery" testId="nav-more-delivery" />
          {canViewQr && (
            <NavTab active={isActive('/qr-codes')} onClick={() => navigate('/qr-codes')} icon={QrCode} label="Price List" testId="nav-more-qr" />
          )}
          {can('purchase_orders.manage') && (
            <NavTab active={isActive('/purchase-orders')} onClick={() => navigate('/purchase-orders')} icon={FileText2} label="Purchase Orders" testId="nav-purchase-orders" />
          )}
          {canOpenAdmin && (
            <>
              <NavTab active={isActive('/admin')} onClick={() => navigate('/admin')} icon={Settings} label="Admin" testId="nav-admin" />
              {/* Health-watchdog badge: only visible on FAILED checks. The
                  status endpoint is admin/manager-only. */}
              {can('system.health') && <HealthStatusBadge />}
            </>
          )}
        </nav>

        {/* ─── Phone menu (hamburger): every page the user has access to. */}
        {mobileOpen && (
          <div className="animate-fade-in border-t border-heda-line/60 bg-white p-2 sm:hidden" data-testid="mobile-nav">
            {PRIMARY_NAV.map(({ path, label, icon, showBadge }) => (
              <MenuRow key={path} icon={icon} onClick={() => go(path)} testId={`mobile-nav-${label.toLowerCase()}`}>
                {label}
                {showBadge && items.length > 0 && (
                  <span className="ml-1 rounded-full bg-heda-gold px-1.5 text-[10px] font-bold text-white">{items.length}</span>
                )}
              </MenuRow>
            ))}
            <MenuRow icon={Truck} onClick={() => go('/delivery')} testId="mobile-nav-delivery">Delivery</MenuRow>
            {canViewQr && <MenuRow icon={QrCode} onClick={() => go('/qr-codes')} testId="mobile-nav-qr">Price List</MenuRow>}
            <MenuRow icon={BarChart3} onClick={() => go('/my-analytics')} testId="mobile-nav-my-analytics">My Analytics</MenuRow>
            <MenuRow icon={AlertCircle} onClick={() => go('/my-complaints')} testId="mobile-nav-my-complaints">My Complaints</MenuRow>
            {showMyRegistrations && (
              <MenuRow icon={ClipboardList} onClick={() => go('/my-registrations')} testId="mobile-nav-my-registrations">My Walk-ins &amp; Leads</MenuRow>
            )}
            {showTeamFollowups && (
              <MenuRow icon={Users} onClick={() => go('/team-followups')} testId="mobile-nav-team-followups">Team follow-ups</MenuRow>
            )}
            {canOpenAdmin && <MenuRow icon={Settings} onClick={() => go('/admin')}>Admin</MenuRow>}
            {can('stock.manage') && <MenuRow icon={Package} onClick={() => go('/stock')}>Stock</MenuRow>}
            {can('purchase_orders.manage') && <MenuRow icon={FileText2} onClick={() => go('/purchase-orders')}>Purchase Orders</MenuRow>}
            {PLATFORM && <MenuRow icon={LayoutGrid} onClick={allApps} testId="mobile-nav-all-apps">All apps</MenuRow>}
            <div className="mt-2 border-t border-heda-line pt-2">
              <MenuRow icon={LogOut} onClick={logout} testId="mobile-logout" className="text-red-700">Logout</MenuRow>
            </div>
          </div>
        )}
      </header>

      <main className="min-h-[calc(100vh-72px)] pb-20 sm:pb-0">
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
          {children}
        </div>
      </main>

      {/* Phone bottom bar — strict 5-slot layout (Left1 · Left2 · Plus ·
          Right1 · Right2); Plus is the middle slot so it's always centred.
          Delivery is intentionally NOT shown here (owner directive). Staff
          see "My Reg" (walk-ins + leads history) in the Admin slot. */}
      <nav
        className="mobile-bottom-nav grid-cols-5 border-t border-heda-line bg-white/95 backdrop-blur sm:hidden"
        data-testid="mobile-bottom-nav"
      >
        {(() => {
          const left = [
            { path: '/', label: 'Dashboard', mobileIcon: Home },
            { path: '/products', label: 'Products', mobileIcon: Search },
          ];
          const right = [
            { path: '/quotations', label: 'History', mobileIcon: FileText },
            canOpenAdmin
              ? { path: '/admin', label: 'Admin', mobileIcon: Settings }
              : { path: '/my-registrations', label: 'My Reg', mobileIcon: ClipboardList },
          ];
          const renderSlot = ({ path, label, mobileIcon: Icon }) => {
            const active = isActive(path);
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`relative flex flex-col items-center justify-center py-1 transition-colors ${active ? 'text-heda-ink' : 'text-heda-mute'}`}
                data-testid={`bottom-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="h-5 w-5" />
                <span className="mt-0.5 text-[10px] font-semibold">{label}</span>
                {active && <span className="absolute bottom-0 h-1 w-1 rounded-full bg-heda-gold" />}
              </button>
            );
          };
          return (
            <>
              {left.map(renderSlot)}
              <button
                onClick={() => navigate('/cart')}
                className="relative flex items-center justify-center"
                data-testid="bottom-nav-quotation"
                aria-label="Quotation"
              >
                <span className="-mt-5 flex h-14 w-14 items-center justify-center rounded-full bg-heda-ink text-white shadow-heda ring-4 ring-heda-cream">
                  <Plus className="h-6 w-6" />
                </span>
                {items.length > 0 && (
                  <span className="absolute -top-2 right-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-heda-gold px-1 text-[10px] font-bold text-white">
                    {items.length}
                  </span>
                )}
              </button>
              {right.map(renderSlot)}
            </>
          );
        })()}
      </nav>
    </div>
  );
}
